/* eslint-disable import/prefer-default-export */
import { useCallback } from 'react';
import axios from 'axios';
import { FormikValues } from 'formik';
import getOmsUrl from '@utils/getOmsUrl';
import { unescapeDeep } from '@utils/html';
import { getFromStorage, removeFromStorage, setOnSessionStorage } from '@utils/sessionStorage';

type UseConsultationsHook = () => {
  getConsultations: (googleIdToken: string) => Promise<any>;
  updateProgress: (body: FormikValues) => void;
  getProgress: (id?: string) => Promise<any>;
  saveProgress: (email: string, completed: boolean) => Promise<any>;
  removeProgress: () => void;
};

export const managePPC = () => {
  const baseUrl = '/health';
  const omsUrl = getOmsUrl();
  const sessionStorageKey = 'period-pain-consultation';

  const useGetQuestionnaire = (): { getPublicQuestionnaire: () => any } => {
    const getPublicQuestionnaire = useCallback(async () => {
      const url = `${omsUrl}${baseUrl}/public/questionnaires`;
      const headers = {
        'Content-Type': 'application/json'
      };
      const res = await axios.get(url, { headers });

      const data = res?.data?.publicQuestionnaires?.[0];
      const unescapedData = data ? unescapeDeep(data) : null;

      return unescapedData;
    }, []);

    return { getPublicQuestionnaire };
  };

  const useConsultations: UseConsultationsHook = () => {
    const getConsultations = useCallback(async (googleIdToken: string) => {
      const url = `${omsUrl}${baseUrl}/consultations/`;
      const headers = {
        token: googleIdToken,
        'Content-Type': 'application/json'
      };
      const res = await axios.get(url, { headers });
      return res.data;
    }, []);

    const updateProgress = useCallback((body: FormikValues) => {
      setOnSessionStorage('period-pain-consultation', body);
    }, []);

    const removeProgress = () => {
      removeFromStorage(sessionStorageKey);
    };

    const getProgress = useCallback(async (id?: string) => {
      if (id) {
        const url = `${omsUrl}${baseUrl}/consultations/${id}`;
        const headers = {
          'Content-Type': 'application/json'
        };
        const res = await axios.get(url, { headers });
        return res.data;
      }

      const data = getFromStorage(sessionStorageKey, false);
      if (data) {
        return {
          data
        };
      }

      return null;
    }, []);

    /**
     * Saves the progress of a Period and Pelvic Pain Clinic (PPC) questionnaire, formerly referred to as a consultation.
     *
     * This function sends a POST request to save the user's progress for a consultation.
     * A "consultation" refers to a filled PPC (Period and Pelvic Pain Clinic) questionnaire.
     * The data is retrieved from session storage and includes the user's email, consultation data,
     * the type of consultation, and whether it is marked as completed.
     *
     * @param {string} email - The email address of the user whose progress is being saved.
     * @param {boolean} completed - Indicates whether the consultation has been completed.
     *
     * @throws {Error} If the POST request fails or if there is an issue with retrieving data from storage.
     */ const saveProgress = useCallback(async (email: string, completed: boolean) => {
      const consultationData = getFromStorage(sessionStorageKey, false);
      if (!consultationData) return;
      const url = `${omsUrl}${baseUrl}/consultations/`;
      const headers = {
        'Content-Type': 'application/json'
      };
      const data = {
        email,
        consultationData,
        type: 'period-pain-consultation',
        completed
      };
      await axios.post(url, data, { headers });
    }, []);

    return {
      getConsultations,
      updateProgress,
      getProgress,
      saveProgress,
      removeProgress
    };
  };

  return { useGetQuestionnaire, useConsultations };
};
